import gql from 'graphql-tag';

const UserPublicBasicFragment = gql`
  fragment UserPublicBasicFragment on User {
    id: primaryIdentityId
    primaryIdentityId
    displayName
    givenName
    familyName
    picture: pictureUrl
    pictureUrl
  }
`;

export const UserQueryPublicBasic = gql`
  query UserQueryPublicBasic($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserPublicBasicFragment
    }
  }
  ${UserPublicBasicFragment}
`;

export const UsersQueryPublicBasic = gql`
  query UsersQueryPublicBasic($ids: [String!]!) {
    profiles: usersByPrimaryIdentityIds(primaryIdentityIds: $ids) {
      ...UserPublicBasicFragment
    }
  }
  ${UserPublicBasicFragment}
`;

export const UserQueryPrivate = gql`
  query UserQuery($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserPublicBasicFragment
      identityIds
      emailAddress
      loginsCount
      location {
        addressSummary
      }
      settings {
        email {
          unsubscribed
        }
        updatedAt
      }
    }
  }
  ${UserPublicBasicFragment}
`;

const UserSavedEventFragment = gql`
  fragment UserSavedEventFragment on UserSavedEvent {
    id
    createdAt
    raceAt
    event {
      id
    }
    reactions {
      id
      comments {
        id
        createdAt
        text
        user {
          id: primaryIdentityId
          primaryIdentityId
          givenName
          familyName
          pictureUrl
        }
      }
      likes {
        id
        user {
          id: primaryIdentityId
          primaryIdentityId
        }
      }
    }
  }
`;

export const UserQueryPublicSavedEvents = gql`
  query UserQueryWithSavedEvents($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserPublicBasicFragment
      savedEvents {
        ...UserSavedEventFragment
      }
    }
  }
  ${UserPublicBasicFragment}
  ${UserSavedEventFragment}
`;

export const UserQueryPublicSavedExternalBookings = gql`
  query UserQueryWithSavedExternalBookings($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserPublicBasicFragment
      savedExternalBookings {
        race {
          id
          event {
            id
          }
        }
      }
      bookings {
        ticket {
          race {
            id
            event {
              id
            }
          }
        }
        isPaymentAuthorized
      }
    }
  }
  ${UserPublicBasicFragment}
`;


export const UserProfileActivityFragment = gql`
  fragment UserProfileActivityFragment on User {
    id: primaryIdentityId
    primaryIdentityId
    givenName
    familyName
    displayName
    pictureUrl
    bookings {
      id
      createdAt
      ticket {
        id
        race {
          id
          startDate
        }
        event {
          id
        }
      }
      reactions {
        id
        comments {
          id
          createdAt
          text
          user {
            id: primaryIdentityId
            primaryIdentityId
            givenName
            familyName
            pictureUrl
          }
        }
        likes {
          id
          user {
            id: primaryIdentityId
            primaryIdentityId
          }
        }
      }
    }
    savedEvents {
      id
      createdAt
      raceAt
      event {
        id
      }
      reactions {
        id
        comments {
          id
          createdAt
          text
          user {
            id: primaryIdentityId
            primaryIdentityId
            givenName
            familyName
            pictureUrl
          }
        }
        likes {
          id
          user {
            id: primaryIdentityId
            primaryIdentityId
          }
        }
      }
    }
    savedExternalBookings {
      id
      createdAt
      raceAt
      event {
        id
      }
      reactions {
        id
        comments {
          id
          createdAt
          text
          user {
            id: primaryIdentityId
            primaryIdentityId
            givenName
            familyName
            pictureUrl
          }
        }
        likes {
          id
          user {
            id: primaryIdentityId
            primaryIdentityId
          }
        }
      }
    }
    reviews {
      id
      createdAt
      review
      year
      race {
        id
      }
      event {
        id
      }
      ratings {
        overall
      }
      reactions {
        id
        comments {
          id
          createdAt
          text
          user {
            id: primaryIdentityId
            primaryIdentityId
            givenName
            familyName
            pictureUrl
          }
        }
        likes {
          id
          user {
            id: primaryIdentityId
            primaryIdentityId
          }
        }
      }
    }
  }
`;

export const UserQueryPublicProfile = gql`
  query UserQueryPublicProfile($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserProfileActivityFragment
      publicConnections: connections {
        type
        user {
          id: primaryIdentityId
          givenName
          familyName
          primaryIdentityId
          pictureUrl
        }
      }
    }
  }
  ${UserProfileActivityFragment}
`;

export const UserQueryPrivateProfile = gql`
  query UserQueryPrivateProfile($id: String!) {
    profile: updateConnections(primaryIdentityId: $id) {
      ...UserProfileActivityFragment
      identityIds
      privateConnections: connections {
        type
        user {
          ...UserProfileActivityFragment
          connections {
            user {
              id: primaryIdentityId
              primaryIdentityId
            }
          }
        }
      }
      unauthenticatedConnections {
        type
        user {
          nativeId
          displayName
          pictureUrl
          invitedAt
        }
      }
    }
  }
  ${UserProfileActivityFragment}
`;

export const UpdateProfileMutation = gql`
  mutation updateProfileMutation(
    $primaryIdentityId: String!, $givenName: String, $familyName: String, $emailAddress: String,
    $location: UserLocationInput, $emailUnsubscribeTypes: [UserEmailUnsubscribeType]
  ) {
    updateUser(input: {
      primaryIdentityId: $primaryIdentityId
      givenName: $givenName
      familyName: $familyName
      emailAddress: $emailAddress
      location: $location
      emailUnsubscribeTypes: $emailUnsubscribeTypes
    }) {
      ...UserPublicBasicFragment
      givenName
      familyName
      identityIds
      emailAddress
      loginsCount
      location {
        addressSummary
      }
      settings {
        updatedAt
        email {
          unsubscribed
        }
        updatedAt
      }
    }
  }
  ${UserPublicBasicFragment}
`;

export const InviteUnauthenticatedConnectionMutation = gql`
  mutation inviteUnauthenticatedConnectionMutation($inviterPrimaryIdentityId: ID!, $nativeIds: [ID!]!) {
    inviteUnauthenticatedConnection(input: {
      inviterPrimaryIdentityId: $inviterPrimaryIdentityId
      nativeIds: $nativeIds
    }) {
      id: primaryIdentityId
      unauthenticatedConnections {
        type
        user {
          nativeId
          displayName
          pictureUrl
          invitedAt
        }
      }
    }
  }
`;

export const getInviteUnauthenticatedConnectionMutationOptimisticResponse = (
  connections,
  profile,
) => ({
  inviteUnauthenticatedConnection: {
    id: profile.primaryIdentityId,
    unauthenticatedConnections: profile.unauthenticatedConnections.map((connection) => {
      const prediction = connections.filter(p => p.user.nativeId === connection.user.nativeId)[0];
      return prediction
        ? { ...prediction, user: { ...prediction.user, invitedAt: 'Just now' } }
        : connection;
    }),
    __typename: 'User',
  },
});

export const LikeActivityMutation = gql`
  mutation likeActivityMutation($activityId: ID!, $activityType: ActivityType!, $activityPrimaryIdentityId: ID!, $eventId: ID!) {
    likeActivity(input: {
      activityId: $activityId
      activityType: $activityType
      activityPrimaryIdentityId: $activityPrimaryIdentityId
      eventId: $eventId
    }) {
      id
      likes {
        id
        user {
          id: primaryIdentityId
          primaryIdentityId
        }
      }
    }
  }
`;

export const getLikeActivityMutationOptimisticResponse = (
  reactions,
  primaryIdentityId,
) => ({
  likeActivity: {
    id: reactions.id,
    likes: (reactions.likes || []).concat(
      [
        {
          id: Date.now(),
          user: {
            id: primaryIdentityId,
            primaryIdentityId,
            __typename: 'User',
          },
          __typename: 'ActivityLike',
        },
      ],
    ),
    __typename: 'ActivityReaction',
  },
});

export const UnlikeActivityMutation = gql`
  mutation unlikeActivityMutation($activityId: ID!, $activityType: ActivityType!, $activityPrimaryIdentityId: ID!, $eventId: ID!) {
    unlikeActivity(input: {
      activityId: $activityId
      activityType: $activityType
      activityPrimaryIdentityId: $activityPrimaryIdentityId
      eventId: $eventId
    }) {
      id
      likes {
        id
        user {
          id: primaryIdentityId
          primaryIdentityId
        }
      }
    }
  }
`;

export const getUnlikeActivityMutationOptimisticResponse = (
  reactions,
  primaryIdentityId,
) => ({
  unlikeActivity: {
    id: reactions.id,
    likes: reactions.likes.filter(l => l.user.primaryIdentityId !== primaryIdentityId),
    __typename: 'ActivityReaction',
  },
});

export const CommentOnActivityMutation = gql`
  mutation commentOnActivityMutation($activityId: ID!, $activityType: ActivityType!, $activityPrimaryIdentityId: ID!, $eventId: ID!, $text: String!) {
    commentOnActivity(input: {
      activityId: $activityId
      activityType: $activityType
      activityPrimaryIdentityId: $activityPrimaryIdentityId
      eventId: $eventId
      text: $text
    }) {
      id
      comments {
        id
        createdAt
        text
        user {
          id: primaryIdentityId
          primaryIdentityId
          givenName
          familyName
          pictureUrl
        }
      }
    }
  }
`;

export const getCommentOnActivityMutationOptimisticResponse = (
  reactions,
  text,
  profile,
) => ({
  commentOnActivity: {
    id: reactions.id,
    comments: (reactions.comments || []).concat([
      {
        id: Date.now(),
        createdAt: (new Date()).toISOString(),
        text,
        user: {
          ...profile,
          __typename: 'User',
        },
        __typename: 'ActivityComment',
      },
    ]),
    __typename: 'ActivityReaction',
  },
});

export const SaveEventMutation = gql`
  mutation saveEventMutation($id: ID!) {
    saveEvent(input: {
      eventId: $id
    }) {
      id: primaryIdentityId
      savedEvents {
        ...UserSavedEventFragment
      }
    }
  }
  ${UserSavedEventFragment}
`;

export const getSaveEventMutationOptimisticResponse = (eventId, user = {}) => ({
  saveEvent: {
    id: user.primaryIdentityId,
    savedEvents: (user.savedEvents || [])
      .concat({
        __typename: 'UserSavedEvent',
        id: '123',
        createdAt: (new Date()).toISOString(),
        event: {
          __typename: 'Event',
          id: eventId,
        },
        raceAt: (new Date()).toISOString(),
        reactions: null,
      }),
    __typename: 'User',
  },
});

export const UnsaveEventMutation = gql`
  mutation unsaveEventMutation($id: ID!) {
    unsaveEvent(input: {
      eventId: $id
    }) {
      id: primaryIdentityId
      savedEvents {
        id
        ...UserSavedEventFragment
      }
    }
  }
  ${UserSavedEventFragment}
`;

export const getUnsaveEventMutationOptimisticResponse = (eventId, user = {}) => ({
  unsaveEvent: {
    id: user.primaryIdentityId,
    savedEvents: (user.savedEvents || [])
      .filter(savedEvent => savedEvent.event.id !== eventId),
    __typename: 'User',
  },
});

export const SaveExternalBookingMutation = gql`
  mutation saveExternalBookingMutation($eventId: ID!, $raceId: ID!) {
    saveExternalBooking(input: {
      eventId: $eventId
      raceId: $raceId
    }) {
      id: primaryIdentityId
      savedExternalBookings {
        id
        race { id event { id } }
      }
    }
  }
`;

export const getSaveExternalBookingMutationOptimisticResponse = (eventId, raceId, user = {}) => ({
  saveExternalBooking: {
    id: user.primaryIdentityId,
    savedExternalBookings: (user.savedExternalBookings || [])
      .concat({
        __typename: 'UserSavedExternalBooking',
        id: '123',
        race: {
          id: raceId,
          event: {
            __typename: 'Event',
            id: eventId,
          },
          __typename: 'Race',
        },
      }),
    __typename: 'User',
  },
});

export const UnsaveExternalBookingMutation = gql`
  mutation unsaveExternalBookingMutation($eventId: ID!, $raceId: ID!) {
    unsaveExternalBooking(input: {
      eventId: $eventId
      raceId: $raceId
    }) {
      id: primaryIdentityId
      savedExternalBookings {
        id
        race { id event { id } }
      }
    }
  }
`;

export const getUnsaveExternalBookingMutationOptimisticResponse = (eventId, raceId, user = {}) => ({
  unsaveExternalBooking: {
    id: user.primaryIdentityId,
    savedExternalBookings: (user.savedExternalBookings || [])
      .filter(savedExternalBooking =>
        !(
          savedExternalBooking.race.event.id === eventId &&
          savedExternalBooking.race.id === raceId
        ),
      ),
    __typename: 'User',
  },
});

const SavedSeachFragment = `
  {
    id
    sportId
    distanceId
    location {
      ... on CenteredLocation {
        addressSummary
        radiusKm
        center {
          latitude
          longitude
        }
      }
      ... on ViewportLocation {
        addressSummary
        northEast {
          latitude
          longitude
        }
        southWest {
          latitude
          longitude
        }
      }
    }
  }
`;

export const UserQueryPublicSavedSearches = gql`
  query UserQueryWithSavedSearches($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserPublicBasicFragment
      savedSearches ${SavedSeachFragment}
    }
  }
  ${UserPublicBasicFragment}
`;

export const SaveSearchMutation = gql`
  mutation saveSearchMutation($search: UserSaveSearchInput!) {
    saveSearch(input: $search) {
      id: primaryIdentityId
      savedSearches ${SavedSeachFragment}
    }
  }
`;

export const SaveSearchesMutation = gql`
  mutation saveSearchesMutation($searches: [UserSaveSearchInput!]!) {
    saveSearches(input: $searches) {
      id: primaryIdentityId
      savedSearches ${SavedSeachFragment}
    }
  }
`;

export const UnsaveSearchMutation = gql`
  mutation unsaveSearchMutation($search: UserUnsaveSearchInput!) {
    unsaveSearch(input: $search) {
      id: primaryIdentityId
      savedSearches ${SavedSeachFragment}
    }
  }
`;

export const EnterCompetitionMutation = gql`
  mutation registerFromReferral($id: ID!, $name: String!){
    registerFromReferral(input: {
      primaryIdentityId: $id,
      name: $name
    }) {
      id: primaryIdentityId
    }
  }
`;

export const VLMQuery = gql`
  query vlmQuery {
    profile: viewer {
      id: primaryIdentityId
      displayName
      competitions {
        londonMarathon2018 {
          referrer {
            timestamp
            points
          }
          referees {
            name
            timestamp
            points
          }
          referrerCode
          bonusTimeout
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./app/src/state/services/gql/user.js